import React, { useState, useEffect } from 'react';
import { filter, find } from 'ramda';
import Modal from '~~elements/Modal';
import Button from '~~elements/Button';
import Select from '~~elements/Select';
import toast from '~~elements/Toast';
import Multilingual from '~~utils/Multilingual';
import { LeagueResource, SeasonResource } from '~~apis/resource';
import { StyledForm } from '../styled';
import { useTournamentDispatch, useTournamentState } from '../useTournament';

const ImportTeamModal = ({ isOpen, toClose, created }) => {
  const { organizerUniqid, tournamentInfo, teams } = useTournamentState();
  const tournamentDispatch = useTournamentDispatch();

  const [tournaments, setTournaments] = useState([]);
  const [pickedTournamentUniqid, setPickedTournamentUniqid] = useState('');

  const [tournamentTeams, setTournamentTeams] = useState(null);
  const [pickedTournamentTeamUniqid, setPickedTournamentTeamUniqid] = useState('');

  const [includePlayers, setIncludePlayers] = useState(false);

  const [apiInProgress, setApiInProgress] = useState(false);

  useEffect(() => {
    setPickedTournamentTeamUniqid('');
    setApiInProgress(false);
  }, [isOpen]);

  useEffect(() => {
    if (!organizerUniqid || !tournamentInfo.uniqid) {
      return;
    }

    LeagueResource.getLeagueWithSeasons({ leagueUniqid: organizerUniqid }).then(({ data }) => {
      setTournaments(data.seasons);
      const filteredTournaments = filter((t) => t.uniqid !== tournamentInfo.uniqid, data.seasons);
      setPickedTournamentUniqid(filteredTournaments.length === 0 ? '' : filteredTournaments[0].uniqid);
    });
  }, [organizerUniqid, tournamentInfo.uniqid]);

  useEffect(() => {
    setTournamentTeams(null);
    setPickedTournamentTeamUniqid('');
    if (!organizerUniqid || !pickedTournamentUniqid) {
      return;
    }

    SeasonResource.getTeams({ seasonUniqid: pickedTournamentUniqid }).then(({ data }) => {
      setTournamentTeams(data);
    });
  }, [organizerUniqid, pickedTournamentUniqid]);

  const ableToImport = () => {
    if (!pickedTournamentUniqid || !pickedTournamentTeamUniqid || !tournamentTeams) {
      return false;
    }

    const tournamentTeam = find((t) => t.uniqid === pickedTournamentTeamUniqid, tournamentTeams);
    if (!tournamentTeam) {
      return false;
    }

    const sameNameInCurrentTournament = find((t) => t.name === tournamentTeam.name, teams);
    if (sameNameInCurrentTournament) {
      return false;
    }

    return true;
  };

  const handleImport = () => {
    if (!ableToImport()) {
      return;
    }

    setApiInProgress(true);
    SeasonResource.importTournamentTeamByOrganizer({
      seasonUniqid: tournamentInfo.uniqid,
      data: {
        from_tournament_uniqid: pickedTournamentUniqid,
        from_tournament_team_uniqid: pickedTournamentTeamUniqid,
        include_players: includePlayers,
      },
    })
      .then(({ data: newTeam }) => {
        SeasonResource.getTeams({ seasonUniqid: tournamentInfo.uniqid })
          .then(({ data: newTeams }) => {
            tournamentDispatch({ type: 'SET_TEAMS', payload: newTeams });
            setTimeout(() => {
              created(newTeam);
            }, 500);
          })
          .catch(() => {
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .finally(() => {
            toast(Multilingual(`SUCCESS.CREATE`), { status: 'success' });
            setApiInProgress(false);
          });
      })
      .catch(() => {
        toast(Multilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
        setApiInProgress(false);
      });
  };

  return (
    <Modal.Modal
      size="sm"
      isOpen={isOpen}
      outterClickBack={!apiInProgress}
      onClickBack={apiInProgress ? () => {} : toClose}
    >
      <Modal.ModalHeader hasBack={!apiInProgress} onClickBack={apiInProgress ? () => {} : toClose}>
        {Multilingual(`TOURNAMENT.IMPORT_FROM_TOURNAMENT.WORDING`)}
      </Modal.ModalHeader>
      <Modal.ModalBody>
        <StyledForm onSubmit={(e) => e.preventDefault()}>
          <div className="box">
            {tournaments.length === 1 && (
              <label>
                <i>{Multilingual(`TOURNAMENT.IMPORT_FROM_TOURNAMENT.NO_OTHER_TOURNAMENTS`)}</i>
              </label>
            )}
            {/* 賽事名稱 */}
            <Select
              label={Multilingual(`TOURNAMENT.TITLE`)}
              value={pickedTournamentUniqid}
              onChange={(e) => setPickedTournamentUniqid(e.target.value)}
            >
              {filter((t) => t.uniqid !== tournamentInfo.uniqid, tournaments).map((tournament) => (
                <option key={tournament.uniqid} value={tournament.uniqid}>
                  {tournament.title}
                </option>
              ))}
            </Select>
            {/* 球隊名稱 */}
            <Select
              label={Multilingual(`TEAM.WORDING`)}
              value={pickedTournamentTeamUniqid}
              onChange={(e) => setPickedTournamentTeamUniqid(e.target.value)}
            >
              <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
              {tournamentTeams &&
                tournamentTeams.map((tournamentTeam) => (
                  <option key={tournamentTeam.uniqid} value={tournamentTeam.uniqid}>
                    {tournamentTeam.name}
                  </option>
                ))}
            </Select>
            <div className="input-field" />
            <label>
              <input type="checkbox" checked={includePlayers} onChange={() => setIncludePlayers(!includePlayers)} />
              {Multilingual(`TOURNAMENT.IMPORT_FROM_TOURNAMENT.INCLUDE_PLAYERS`)}
            </label>
          </div>
        </StyledForm>
      </Modal.ModalBody>
      <Modal.ModalFooter>
        {pickedTournamentUniqid &&
          pickedTournamentTeamUniqid &&
          (!ableToImport() ? (
            <span style={{ color: 'red' }}>
              {Multilingual(`TOURNAMENT.IMPORT_FROM_TOURNAMENT.TEAM_NAME_DUPLICATE`)}
            </span>
          ) : (
            <Button width="10rem" color="red" onClick={handleImport}>
              {Multilingual(`TOURNAMENT.IMPORT_FROM_TOURNAMENT.IMPORT`)}
            </Button>
          ))}
      </Modal.ModalFooter>
    </Modal.Modal>
  );
};

export default ImportTeamModal;
